import PropTypes from "prop-types";
import tags from "@constants/sitemaps/tags";

import { Pagination } from "@components/pagination";

import { apiApolloFetch } from "@queries/apiApolloFetch";
import ARTICLES_BY_TAGS from "@queries/sitemap/articles.gql";

import SitemapPage, { breadcrumbs } from "../index.page";

export default function SitemapArticlesDetail({ metadata, items }) {
  return (
    <SitemapPage metadata={metadata}>
      <ul className="divide-y-2 divide-blue-100">
        {items.map(item => (
          <li className="py-8">
            <p className="text-sm text-slate">{item.date}</p>
            <a
              href={`/articles/${item.slug}`}
              className="inline-block my-2 text-lg text-black font-semibold"
            >
              {item.title}
            </a>
            <ul className="flex flex-wrap gap-2">
              {item.tags.map(tag => (
                <li>
                  <span className="text-xs rounded-pill px-3 py-1 bg-blue-100 text-blue tracking-wide">
                    {tag.title}
                  </span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>

      {metadata.pages > 1 && (
        <div className="sticky bottom-0 bg-white divide-none py-2">
          <Pagination
            formSlug={metadata.category}
            selectedPage={metadata.page}
            numPages={metadata.pages}
          />
        </div>
      )}
    </SitemapPage>
  );
}

export async function getServerSideProps({ query, resolvedUrl }) {
  const { category, page = 1 } = query;
  const pagesize = 100;

  /**
   * There is a potential DDoS attack vector where endlessly increasing
   * the page param's value on request will fire expensive queries
   * indefinately. There isn't a straight forward way to set a dynamic
   * maximum per request type but we can generally know how many pages
   * of content we have, so we will set that  soft cap as a means to
   * mitigate the issue by not allowing _enough_ range to effectively
   * tip-over the servers.
   */

  /* eslint-disable camelcase */
  const magic_page_limit = 60;
  if (page > magic_page_limit || Number.isNaN(Number(page))) {
    return {
      notFound: true,
    };
  }

  const tag = tags.find(
    t => t.slug === category || t.children.some(c => c.slug === category)
  );

  const querytags =
    tag.slug === category
      ? [...tag.children.map(child => child.slug), category]
      : category;

  const { data } = await apiApolloFetch({
    context: {
      uri: `${process.env.NEXT_PUBLIC_LPAPI}/consumer/graphql/v1`,
      headers: {
        "x-api-key": process.env.NEXT_PUBLIC_LPAPI_KEY,
      },
    },
    query: ARTICLES_BY_TAGS,
    variables: {
      size: pagesize,
      offset: (page - 1) * pagesize || 0,
      filter: {
        meta: {
          coreContentTag: { any: tag.core ? querytags : null },
          type: {
            slug: { in: ["affiliate_review", "feature", "kids-blog-post"] },
          },
        },
        tag: { slug: { any: tag.core ? null : querytags } },
      },
    },
  });

  const { count, items } = data.articles;

  return {
    props: {
      metadata: {
        title: `${category.charAt(0).toUpperCase() + category.slice(1)} Articles`,
        breadcrumbs: breadcrumbs(resolvedUrl),
        category,
        page: Number(page) || 1,
        pages: Math.floor(count / pagesize) + 1,
      },
      items: items.map(item => ({
        title: item.title,
        slug: item.slug,
        tags: item.tags?.slice(0, 5) || [],
        date: new Date(item.date).toLocaleDateString("en-us", {
          month: "long",
          day: "numeric",
          year: "numeric",
        }),
      })),
    },
  };
}

SitemapArticlesDetail.propTypes = {
  metadata: PropTypes.shape({
    title: PropTypes.string.isRequired,
    breadcrumbs: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        path: PropTypes.string.isRequired,
      })
    ),
    // page-specific
    page: PropTypes.number.isRequired,
    pages: PropTypes.number.isRequired,
    category: PropTypes.string.isRequired,
  }).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      tags: PropTypes.arrayOf(
        PropTypes.shape({ title: PropTypes.string.isRequired })
      ).isRequired,
    })
  ).isRequired,
};
